<template>
	<BlogRoot
		v-if="!isLoading && list && list.length > 0"
		:posts="list"
		:current-page="Number(pageNumber)"
		:previous-page="Number(previousPageNumber)"
		:total-pages="Number(total_pages)"
	/>
	<div
		v-else
		class="h-[600px]"
	/>
	<Head>
		<Title>Timothy Sykes Blog - Penny Stocks Trading Success Guide</Title>
		<Link
			rel="canonical"
			:href="current_hostname + 'blog/'"
		/>

		<template v-if="head_seo">
			<template
				v-for="(tag, i) in head_seo"
				:key="i"
			>
				<Meta
					v-if="tag[0].startsWith('og_')"
					:property="'og:' + tag[0].slice(3)"
					:content="tag[1][0].url || tag[1]"
				/>

				<Meta
					v-else-if="tag[0].startsWith('article')"
					:property="'article:' + tag[0].slice(8)"
					:content="tag[1]"
				/>
				<Meta
					v-else-if="tag[0] !== 'twitter_misc' && tag[0].startsWith('twitter')"
					:name="'twitter:' + tag[0].slice(8)"
					:content="tag[1]"
				/>

				<template v-else-if="tag[0] === 'twitter_misc'">
					<Meta
						:name="'twitter:label1'"
						:content="Object.keys(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:data2'"
						:content="Object.values(tag[1])[0]"
					/>
					<Meta
						:name="'twitter:label2'"
						:content="Object.keys(tag[1])[1]"
					/>
					<Meta
						:name="'twitter:data1'"
						:content="Object.values(tag[1])[1]"
					/>
				</template>

				<Meta
					v-else-if="tag[0] != 'og_image'"
					:name="tag[0]"
					:content="tag[1]"
				/>
				<template v-else>
					<Meta
						:property="tag[0].split('_').join(':') + ':type'"
						:content="tag[1][0].type"
					/>
					<Meta
						:property="tag[0].split('_').join(':')"
						:content="tag[1][0].url"
					/>
					<Meta
						:property="tag[0].split('_').join(':') + ':width'"
						:content="tag[1][0].width"
					/>
					<Meta
						:property="tag[0].split('_').join(':') + ':height'"
						:content="tag[1][0].height"
					/>
				</template>
			</template>
		</template>
	</Head>
</template>

<script setup>
import axios from "axios";
const $route = useRoute();
const $route_name = ref("");

const $config = useRuntimeConfig();
const hostname = $config.public.hostname;
const { current_hostname } = $config.public;

const blogList = useBlogListFetch();

const { data } = await useAsyncData(
	"page_seo_data",
	async () => {
		const params = $route.params;
		const routePath = $route.path;

		if (routePath === "/news/") {
			await navigateTo("/news/");
			return { redirected: true, url: "/news/" };
		}
		if (params.categoryslug) {
			const path = `/category/${params.categoryslug[0]}/`;
			await navigateTo(path);
			return { redirected: true, url: path };
		}
		const { data } = await axios.get(`${hostname}wp-json/wp/v2/pages/9014/`);

		const { yoast_head_json, yoast_meta } = data;
		const { schema_json } = data.yoast_head_json;

		let parsed_schema;
		if (schema_json) {
			parsed_schema = JSON.parse(schema_json);
			const parsed_schema_id = parse_saswpSchema(
				parsed_schema["@graph"],
				$config.public.hostname,
				$config.public.current_hostname
			);

			parsed_schema["@graph"] = parsed_schema_id;
		}
		const result = await blogList;

		if (result.error) {
			return null;
		}

		return {
			yoast_head_json,
			yoast_meta,
			schema_json: parsed_schema,
			...result
		};
	},
	{
		getCachedData(key, nuxtApp) {
			const cached = nuxtApp.payload.data[key] ?? nuxtApp.static?.data?.[key];
			if (cached) return cached;

			// During hydration, payload.data may be empty. Fall back to useState (in payload.state).
			if (nuxtApp.isHydrating && process.client) {
				const stateKey = "$sssr_blog_page";
				const ssrState = nuxtApp.payload?.state?.[stateKey];
				if (ssrState) return ssrState;
			}

			return undefined;
		}
	}
);

// Bridge SSR data to client via useState (payload.data is empty during hydration,
// but payload.state used by useState IS available — confirmed via debug logs)
const ssrBlogPageData = useState("ssr_blog_page", () => data.value ?? null);

// Keep ssrData updated when useAsyncData resolves on client
watch(
	() => data.value,
	(newData) => {
		if (newData) ssrBlogPageData.value = newData;
	},
	{ immediate: false }
);

// Use computed properties with useState fallback for hydration
const yoast_head_json = computed(
	() =>
		data.value?.yoast_head_json ?? ssrBlogPageData.value?.yoast_head_json ?? null
);
const schema_json = computed(
	() => data.value?.schema_json ?? ssrBlogPageData.value?.schema_json ?? null
);
const list = computed(
	() => data.value?.list ?? ssrBlogPageData.value?.list ?? []
);
const pageNumber = computed(
	() => data.value?.pageNumber ?? ssrBlogPageData.value?.pageNumber ?? 1
);
const previousPageNumber = computed(
	() =>
		data.value?.previousPageNumber ??
		ssrBlogPageData.value?.previousPageNumber ??
		null
);
const total_pages = computed(
	() => data.value?.total_pages ?? ssrBlogPageData.value?.total_pages ?? 1
);

// isLoading uses list (which has useState fallback) to prevent hydration mismatch
const isLoading = computed(() => !list.value || list.value.length === 0);

const head_seo = computed(() => {
	if (yoast_head_json.value) {
		const filtered = Object.entries(yoast_head_json.value).filter(
			(item) =>
				![
					"title",
					"robots",
					"canonical",
					"schema_json",
					"WordPressAuthor_YoastHeadJson_Schema_"
				].includes(item[0])
		);
		// Replace "Blog- Timothy Sykes" with "Timothy Sykes Blog" in meta values
		filtered.forEach((item) => {
			if (typeof item[1] === "string") {
				item[1] = item[1].replace(/Blog-\s*Timothy Sykes/g, "Timothy Sykes Blog");
			}
		});
		return filtered;
	}

	return null;
});

onMounted(async () => {
	$route_name.value = $route.name;

	if (list.value && list.value.length > 0 && process.client) {
		await setTimeout(() => window.scrollTo(0, 1), 1000);
	}
});

useHead(() => ({
	script: [
		{
			type: "application/ld+json",
			class: "saswp-schema-markup-output",
			innerHTML: JSON.stringify(schema_json.value)
		}
	]
}));
</script>
